import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { tx, uiLocale } from "@/i18n";

const CATS = [["none", "Hoort er niet bij"], ["checkin", "Incheck"], ["checkout", "Uitcheck"], ["cleaning", "Schoonmaak"]];
const DAY = 86400000;
const fmt = (s) => new Date(s).toLocaleString(uiLocale(), { weekday: "short", day: "numeric", month: "short", ...(s.includes("T") ? { hour: "2-digit", minute: "2-digit" } : {}) });

export const MakeReservationDialog = ({ seed, bookings, onClose, onDone }) => {
  const candidates = useMemo(() => {
    const t0 = new Date(seed.start).getTime();
    return (bookings || []).filter((b) => b.house_id === seed.house_id && !b.reservation_id && Math.abs(new Date(b.start).getTime() - t0) <= 21 * DAY)
      .sort((a, b) => a.start.localeCompare(b.start));
  }, [seed, bookings]);
  const [cat, setCat] = useState({ [seed.id]: ["checkin", "checkout", "cleaning"].includes(seed.type) ? seed.type : "checkin" });
  const [f, setF] = useState({ guest_name: seed.guest_name || "", guest_email: "", guest_phone: "", num_guests: 2, register_ses: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const items = Object.entries(cat).filter(([, c]) => c && c !== "none").map(([booking_id, category]) => ({ booking_id, category }));
  const counts = items.reduce((a, i) => ({ ...a, [i.category]: (a[i.category] || 0) + 1 }), {});
  const valid = counts.checkin === 1 && counts.checkout === 1 && (counts.cleaning || 0) <= 1 && f.guest_name.trim();

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reservations/from-bookings", { ...f, guest_email: f.guest_email || null, num_guests: Number(f.num_guests) || 1, items });
      toast.success(tx("Boeking aangemaakt met checklists"));
      onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="make-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{tx("Maak boeking van losse afspraken")}</DialogTitle>
          <DialogDescription>{tx("Kies welke afspraken bij deze boeking horen. Ze krijgen de checklist uit het sjabloon.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="make-res-candidates">
          {candidates.map((b) => (
            <div key={b.id} className="flex items-center gap-2 rounded-lg border border-border p-2">
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-medium truncate">{b.title}</div>
                <div className="text-xs text-muted-foreground">{fmt(b.start)}</div>
              </div>
              <Select value={cat[b.id] || "none"} onValueChange={(v) => setCat((p) => ({ ...p, [b.id]: v }))}>
                <SelectTrigger className="w-40 h-9" data-testid={`make-res-cat-${b.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(([k, l]) => <SelectItem key={k} value={k}>{tx(l)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="col-span-2"><Label>{tx("Naam gast")}</Label><Input value={f.guest_name} onChange={set("guest_name")} data-testid="make-res-name" /></div>
          <div className="col-span-2"><Label>{tx("E-mail gast")}</Label><Input type="email" value={f.guest_email} onChange={set("guest_email")} data-testid="make-res-email" /></div>
          <div><Label>{tx("Mobiel")}</Label><Input value={f.guest_phone} onChange={set("guest_phone")} placeholder="+31612345678" data-testid="make-res-phone" /></div>
          <div><Label>{tx("Aantal personen")}</Label><Input type="number" min={1} value={f.num_guests} onChange={set("num_guests")} data-testid="make-res-guests" /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><Checkbox checked={f.register_ses} onCheckedChange={(v) => set("register_ses")(!!v)} data-testid="make-res-ses" /> {tx("Aanmelden SES vereist")}</label>
        </div>
        <p className="text-xs text-muted-foreground">{tx("Met een e-mailadres krijgt de gast de gastomgeving en de automatische mails (als die aan staan).")}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{tx("Annuleren")}</Button>
          <Button onClick={submit} disabled={!valid || busy} className="bg-primary hover:bg-primary/90" data-testid="make-res-submit">{tx("Maak boeking")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
